import React from 'react'
import { api } from '@api/index'
import AdminSidebar from '@components/admin/AdminSidebar'
import MessageModal from '@components/shared/MessageModal'
import useModal from '@hooks/useModal'
import { Message } from '@utils/message'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import AdminPostForm from '@components/admin/AdminPostForm'
import 'twin.macro'

const renderNoticeState = (startDateStr: string, endDateStr: string) => {
  const startDate = new Date(startDateStr)
  const currendDate = new Date()
  const endDate = new Date(endDateStr)

  if (currendDate < startDate) {
    return (
      <div tw="text-yellow-600 flex items-center gap-8">
        <div tw="w-15 h-15 rounded-full bg-yellow-600"></div> 예정
      </div>
    )
  }
  if (startDate <= currendDate && currendDate <= endDate) {
    return (
      <div tw="text-green-600 flex items-center gap-8">
        <div tw="w-15 h-15 rounded-full bg-green-600"></div> 진행중
      </div>
    )
  }
  if (currendDate > endDate) {
    return (
      <div tw="text-red-500 flex items-center gap-8">
        <div tw="w-15 h-15 rounded-full bg-red-500"></div> 마감
      </div>
    )
  }
}

const AdminNoticeAdd = () => {
  const queryClient = useQueryClient()
  const { id: noticeUUID } = useParams()
  const [openResModal] = useModal()
  const navigate = useNavigate()

  const methods = useForm({
    mode: 'onChange',
  })

  const { handleSubmit, setValue } = methods

  const {
    isLoading,
    error,
    data: noticeInfo,
  } = useQuery(
    ['NoticeDetail'],
    () => api.adminService.getNoticeDetail(Number(noticeUUID)),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        Object.entries(data).forEach(([key, value]) => {
          setValue(key, value)
        })
      },
    }
  )

  const modifyNoticeMutation = useMutation(
    (formData: FieldValues) =>
      api.adminService.modifyNoticeDetail(formData as NoticeInfo),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['NoticeDetail'])
      },
    }
  )

  const deleteNoticeMutation = useMutation((noticeUUID: number) =>
    api.adminService.deleteNotice(noticeUUID)
  )

  const handleModifyClick = (form: FieldValues) => {
    modifyNoticeMutation.mutate(form, {
      onSuccess: async () => {
        await openResModal(
          <MessageModal>{Message.info.SUCCESS_MODIFY_NOTICE}</MessageModal>
        )
      },
    })
  }

  const handleDeleteClick = async () => {
    const res = await openResModal(
      <MessageModal>{Message.question.DELETE_NOTICE}</MessageModal>
    )
    if (!res) return
    deleteNoticeMutation.mutate(Number(noticeUUID), {
      onSuccess: async () => {
        await openResModal(
          <MessageModal>{Message.info.SUCCESS_DELETE_NOTICE}</MessageModal>
        )
        navigate('/admin/notice-management')
      },
    })
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleModifyClick)}>
        <section tw="w-1200 flex gap-32">
          <AdminSidebar />
          <main tw="w-full ">
            <h1 tw="text-2xl border-b-2 font-bold pb-10 mb-20 border-neutral-400">
              공지사항 수정
            </h1>
            <div tw="w-full px-2 py-3">
              <AdminPostForm
                stateLabel={
                  noticeInfo?.startDate &&
                  renderNoticeState(noticeInfo?.startDate, noticeInfo?.endDate)
                }
              />
              <div tw="w-full flex justify-end my-15 gap-12">
                <button
                  onClick={handleDeleteClick}
                  form="user-modify-form"
                  type="button"
                  tw="py-4 px-20"
                  className="button"
                >
                  삭제
                </button>
                <button type="submit" tw="py-4 px-20" className="button">
                  수정
                </button>
              </div>
            </div>
          </main>
        </section>
      </form>
    </FormProvider>
  )
}

export default AdminNoticeAdd
